import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Building2, Receipt, Calculator, ArrowRight, Info } from 'lucide-react';

const SetupPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { updateBusinessSettings, user } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    business_name: '',
    tin_number: '',
    tax_type: 'presumptive',
    address: '',
    phone: '',
    monthly_rent: '',
    other_fixed_expenses: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      await updateBusinessSettings({
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        other_fixed_expenses: parseFloat(formData.other_fixed_expenses) || 0,
        vat_rate: 18.0,
        vat_threshold: 150000000,
        has_employees: false,
        employees: [],
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-semibold">Business Information</h2>
        <p className="text-muted-foreground mt-2">Let's start with your business details</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="business_name">Business Name *</Label>
          <Input
            id="business_name"
            placeholder="e.g., Kampala Mini Supermarket"
            value={formData.business_name}
            onChange={(e) => handleChange('business_name', e.target.value)}
            className="h-12"
            data-testid="setup-business-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tin_number">TIN Number (Optional)</Label>
          <Input
            id="tin_number"
            placeholder="e.g., 1000000000"
            value={formData.tin_number}
            onChange={(e) => handleChange('tin_number', e.target.value)}
            className="h-12"
            data-testid="setup-tin-number"
          />
          <p className="text-sm text-muted-foreground">
            Tax Identification Number from URA
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Business Phone</Label>
          <Input
            id="phone"
            placeholder="e.g., 0772123456"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="h-12"
            data-testid="setup-phone"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Business Address</Label>
          <Input
            id="address"
            placeholder="e.g., Plot 45, Kampala Road"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            className="h-12"
            data-testid="setup-address"
          />
        </div>
      </div>

      <Button
        onClick={() => setStep(2)}
        className="w-full h-12 text-lg font-semibold"
        disabled={!formData.business_name}
        data-testid="setup-next-step1"
      >
        Continue
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/10 mb-4">
          <Receipt className="h-8 w-8 text-secondary" />
        </div>
        <h2 className="font-heading text-2xl font-semibold">Tax Classification</h2>
        <p className="text-muted-foreground mt-2">Choose your URA tax type</p>
      </div>

      <div className="space-y-4">
        <RadioGroup
          value={formData.tax_type}
          onValueChange={(value) => handleChange('tax_type', value)}
          className="space-y-4"
        >
          <div 
            className={`flex items-start space-x-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              formData.tax_type === 'presumptive' 
                ? 'border-primary bg-primary/5' 
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => handleChange('tax_type', 'presumptive')}
          >
            <RadioGroupItem value="presumptive" id="presumptive" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="presumptive" className="text-lg font-semibold cursor-pointer">
                Presumptive Tax (Small Business)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                For businesses with annual turnover below UGX 150 million. 
                Pay a fixed annual tax based on your turnover band.
              </p>
              <div className="mt-2 p-2 bg-background/70 border border-border/60 rounded text-sm shadow-sm">
                <strong>Tax Bands:</strong>
                <br />• UGX 10-30M: UGX 100,000/year
                <br />• UGX 30-50M: UGX 200,000/year
                <br />• UGX 50-80M: UGX 350,000/year
                <br />• UGX 80-150M: UGX 600,000/year
              </div>
            </div>
          </div>

          <div 
            className={`flex items-start space-x-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              formData.tax_type === 'vat_registered' 
                ? 'border-primary bg-primary/5' 
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => handleChange('tax_type', 'vat_registered')}
          >
            <RadioGroupItem value="vat_registered" id="vat_registered" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="vat_registered" className="text-lg font-semibold cursor-pointer">
                VAT Registered
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                For businesses with annual turnover above UGX 150 million, 
                or voluntarily registered. Charge 18% VAT on sales.
              </p>
              <div className="mt-2 p-2 bg-background/70 border border-border/60 rounded text-sm shadow-sm">
                <strong>Requirements:</strong>
                <br />• File monthly VAT returns
                <br />• Issue tax invoices
                <br />• EFRIS compliance mandatory
              </div>
            </div>
          </div>
        </RadioGroup>

        <Alert className="bg-secondary/10 border-secondary/30">
          <Info className="h-4 w-4 text-secondary" />
          <AlertDescription className="text-sm">
            <strong>Not sure which to choose?</strong> If your annual sales are below 
            UGX 150 million, Presumptive Tax is simpler and usually cheaper.
          </AlertDescription>
        </Alert>
      </div>

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => setStep(1)}
          className="flex-1 h-12"
          data-testid="setup-back-step2"
        >
          Back
        </Button>
        <Button
          onClick={() => setStep(3)}
          className="flex-1 h-12 text-lg font-semibold"
          data-testid="setup-next-step2"
        >
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
          <Calculator className="h-8 w-8 text-destructive" />
        </div>
        <h2 className="font-heading text-2xl font-semibold">Fixed Expenses</h2>
        <p className="text-muted-foreground mt-2">
          Know your break-even point to survive
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="monthly_rent">Monthly Rent (UGX)</Label>
          <Input
            id="monthly_rent"
            type="number"
            placeholder="e.g., 500000"
            value={formData.monthly_rent}
            onChange={(e) => handleChange('monthly_rent', e.target.value)}
            className="h-12 font-mono"
            data-testid="setup-rent"
          />
          <p className="text-sm text-muted-foreground">
            Your monthly shop/office rent
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="other_fixed_expenses">Other Fixed Monthly Expenses (UGX)</Label>
          <Input
            id="other_fixed_expenses"
            type="number"
            placeholder="e.g., 200000"
            value={formData.other_fixed_expenses}
            onChange={(e) => handleChange('other_fixed_expenses', e.target.value)}
            className="h-12 font-mono"
            data-testid="setup-expenses"
          />
          <p className="text-sm text-muted-foreground">
            Utilities, salaries, licenses, etc.
          </p>
        </div>

        {(formData.monthly_rent || formData.other_fixed_expenses) && (
          <div className="p-4 bg-card/90 rounded-lg border border-border/60 shadow-sm">
            <h3 className="font-semibold mb-2">Your Break-Even Points</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Monthly</p>
                <p className="text-2xl font-mono font-bold text-primary">
                  UGX {((parseFloat(formData.monthly_rent) || 0) + (parseFloat(formData.other_fixed_expenses) || 0)).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Daily</p>
                <p className="text-2xl font-mono font-bold text-primary">
                  UGX {(((parseFloat(formData.monthly_rent) || 0) + (parseFloat(formData.other_fixed_expenses) || 0)) / 30).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              You need to make at least this much in sales every day/month to cover your fixed costs.
            </p>
          </div>
        )}

      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => setStep(2)}
          className="flex-1 h-12"
          data-testid="setup-back-step3"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="flex-1 h-12 text-lg font-semibold neon-green"
          disabled={loading}
          data-testid="setup-complete-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Setting up...
            </>
          ) : (
            'Complete Setup'
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3">
            <img
              src="/survipos%20logo.png"
              alt="SurviPOS logo"
              className="h-12 w-12 object-cover"
            />
            <h1 className="font-heading text-4xl font-bold text-primary">SurviPOS</h1>
          </div>
          <p className="text-muted-foreground">by Kareem Data Labs</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 w-16 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        <Card className="border-border/60 bg-card/75 shadow-xl backdrop-blur">
          <CardContent className="pt-6">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Welcome, <span className="text-primary font-medium">{user?.full_name}</span>!
          <br />
          Let's set up your business for success.
        </p>
      </div>
    </div>
  );
};

export default SetupPage;
